import { useParams, useSearchParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import {
    GridElementFilterResult,
    useVCycleElementContainerFilterActions,
} from '../../../resource/resources/persistables/user/vcycleElementContainerFilterResource';
import { SomeRouteInfo } from '../../router/createdRouter';
import { useProjectVersionActions } from '../../../resource/resources/artifacts/project/projectVersionResource';
import {
    useProjectVersionPublishmentActions,
} from '../../../resource/resources/artifacts/project/projectVersionPublishmentResource';
import {
    useWorkflowRepresentationActions,
} from '../../../resource/resources/artifacts/use_case/representation/workflowRepresentationResource';
import { ActionIcon, Affix, Button, Card, Group, Tooltip } from '@mantine/core';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import { LinkCopyButtonComponent } from '../../reusable-components/LinkCopyButtonComponent';
import { FilterBarComponent } from './vcycle-viewer/FilterBarComponent';
import { OpenModal } from '../../../../../react-commons/utils/modals/openModal';
import { AssociatedRolesInWorkflowComponent } from './vcycle-viewer/AssociatedRolesInWorkflowComponent';
import { IconPlus, IconUsersGroup } from '@tabler/icons-react';
import RGL, { WidthProvider } from 'react-grid-layout';
import {
    GridElementResource,
    useGridElementActions,
} from '../../../resource/resources/artifacts/use_case/representation/gridElementResource';
import { GridElementCreationDialog } from './grid-element/GridElementCreationDialog';
import { useNestedDialogFunction } from '../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { useProcessAreaActions } from '../../../resource/resources/artifacts/process_area/processAreaResource';
import { GridElementEditDialog } from './grid-element/GridElementEditDialog';
import { useDeleteResourceDialogFunction } from '../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import { GridElementNode } from './grid-element/GridElementNode';

const ReactGridLayout = WidthProvider(RGL);

const getWindowDimensions = () => {
    const { innerWidth: width, innerHeight: height } = window;
    return {
        width,
        height,
    };
};

export const GridRepresentationViewerComponent = ({ inTabs, workflowId, useCaseObjectId, editMode }: {
    inTabs: number | undefined,
    workflowId: string,
    useCaseObjectId: string
    editMode: boolean
}) => {
    const nestedDialog = useNestedDialogFunction();
    const { id: projectId, versionId, standardId, useCaseId, workflowObjectId } = useParams<{
        id: string,
        versionId: string,
        standardId: string,
        useCaseId: string,
        workflowObjectId: string,
    }>();
    const [search] = useSearchParams();
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
    const [nodes, setNodes] = useState<Array<GridElementResource>>([]);
    const [nodesToUpdate, setNodesToUpdate] = useState<Array<RGL.Layout>>([]);
    const [appliedFilter, setAppliedFilter] = React.useState<GridElementFilterResult | null>(null);

    const deleteResouceFunction = useDeleteResourceDialogFunction();

    const projectVersionActions = useProjectVersionActions();
    const vcycleContainerFilterActions = useVCycleElementContainerFilterActions();
    const projectVersionPublishmentActions = useProjectVersionPublishmentActions();
    const workflowActions = useWorkflowRepresentationActions();
    const gridElementActions = useGridElementActions();
    const processAreaActions = useProcessAreaActions();
    const projectVersionQuery = projectVersionActions.useGetByPublishment(search.get('publishment'), projectId);
    const projectVersionPublishmentQuery = projectVersionPublishmentActions.useGetSingleResource(versionId ?? projectVersionQuery.data?.id);
    const workflowByIdQuery = workflowActions.useGetResource(workflowId);
    const gridElementsQuery = gridElementActions.useGetAllResources(workflowId);
    const processAreasQuery = processAreaActions.useGetAllResources(versionId ?? projectVersionQuery.data?.id);
    const updateGridElement = gridElementActions.useUpdateResource();
    const deleteGridElement = gridElementActions.useDeleteResource();
    const applyFilterFunction = vcycleContainerFilterActions.useApplyGridFilter();

    const link = location.href.substring(0, location.href.indexOf('project')) + `project/${projectId}/published-use-case/${useCaseObjectId}/published-workflow-representation/${workflowByIdQuery.data?.objectId}?publishment=${encodeURIComponent(search.get('publishment') ?? projectVersionPublishmentQuery.data?.title ?? '')}`;

    const openAddDialog = () => {
        nestedDialog.showNestedDialog(
            <GridElementCreationDialog
                projectId={projectId}
                projectVersionId={versionId ?? projectVersionQuery.data?.id}
                onClose={nestedDialog.closeNestedDialog}
                parentId={workflowId}
                onCreated={() => setNodesToUpdate([])}
            />,
        );
    };

    const openEditDialog = (objectId: string) => {

        const handleEdit = () => {
            nestedDialog.showNestedDialog(
                <GridElementEditDialog
                    projectId={projectId}
                    versionedInId={versionId ?? projectVersionQuery.data?.id ?? ''}
                    onClose={nestedDialog.closeNestedDialog}
                    objectId={objectId}
                    onSuccess={() => {
                        resetChanges();
                        nestedDialog.closeNestedDialog();
                    }}
                />,
            );
        };

        if (nodesToUpdate.length) {
            OpenModal.AlertModal(
                'Unsaved Grid Changes',
                'There are unsaved grid changes. Trying to edit a grid element will reset all unsaved changes. Are you sure you want to do this?',
                handleEdit,
            );
        } else {
            handleEdit();
        }
    };

    const deleteNode = (id: string) => {

        const deleteResource = () => {
            deleteResouceFunction.deleteResource(
                id, null, ResourceType.GRID_ELEMENT, deleteGridElement, () => setNodesToUpdate([]),
            );
        };

        if (nodesToUpdate.length) {
            OpenModal.AlertModal(
                'Unsaved Grid Changes',
                'There are unsaved grid changes. Trying to delete a grid element will reset all unsaved changes. Are you sure you want to do this?',
                deleteResource,
            );
        } else {
            deleteResource();
        }
    };

    const resetChanges = () => {
        setNodesToUpdate([]);
        setNodes([]);
        setTimeout(() => {
            setNodes(gridElementsQuery.data ?? []);
        }, 100);
    };

    const routeInfo = (processAreaObjectId: string) => {
        const publishmentTitle = search.get('publishment') ?? undefined;
        return (
            (publishmentTitle ? ({
                route: '/project/:id/published-process-area/:processAreaObjectId/published-use-case/:useCaseObjectId/published-workflow-representation/:workflowObjectId',
                routeParams: {
                    id: projectId ?? '',
                    processAreaObjectId: processAreaObjectId,
                    useCaseObjectId: useCaseObjectId,
                    workflowObjectId: workflowObjectId ?? '',
                },
                searchParams: { publishment: publishmentTitle },
            }) : ({
                route: '/project/:id/active-version/:versionId/process-area/:processAreaId/standard/:standardId/use-case/:useCaseId/workflow-representation/:workflowId',
                routeParams: {
                    id: projectId ?? '',
                    versionId: versionId ?? projectVersionQuery.data?.id ?? '',
                    processAreaId: processAreasQuery.data?.find(value => value.objectId === processAreaObjectId)?.id ?? '',
                    standardId: standardId ?? '',
                    useCaseId: useCaseId ?? '',
                    workflowId: workflowId,
                },
                searchParams: {},
            }))
        ) satisfies SomeRouteInfo;
    };

    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setNodes(gridElementsQuery.data ?? []);
    }, [gridElementsQuery.data, appliedFilter]);

    return (
        <Card style={{
            width: windowDimensions.width,
            height: windowDimensions.height - 60,
            backgroundColor: 'var(--mantine-color-body)',
        }}>
            <Show when={editMode}>
                <Affix position={{ top: inTabs, right: 50 }}>
                    <Tooltip label={'Add Element'}>
                        <ActionIcon
                            onClick={() => {
                                if (nodesToUpdate.length) {
                                    OpenModal.AlertModal(
                                        'Unsaved Grid Changes',
                                        'There are unsaved grid changes. Trying to add a grid element will reset all unsaved changes. Are you sure you want to do this?',
                                        () => openAddDialog(),
                                    );
                                } else {
                                    openAddDialog();
                                }
                            }}
                            variant={'default'}
                            color={'var(--mantine-color-base-button)'}
                        >
                            <IconPlus />
                        </ActionIcon>
                    </Tooltip>
                </Affix>
            </Show>
            <Show
                when={projectVersionPublishmentQuery.data && inTabs !== undefined}
            >
                <Affix position={{ top: inTabs, right: 50 }}>
                    <Tooltip label={'Copy Link'}>
                        <LinkCopyButtonComponent link={link} variant={'default'} />
                    </Tooltip>
                </Affix>
            </Show>
            <Show when={inTabs !== undefined}>
                <Affix position={{ top: inTabs, left: 15 }}>
                    <FilterBarComponent
                        onApplyFilter={(value) => {
                            if (!value) {
                                setAppliedFilter(null);
                            } else {
                                applyFilterFunction.mutate({
                                    id: workflowId,
                                    filterResource: value,
                                }, {
                                    onSuccess: (data) => {
                                        setAppliedFilter(data);
                                    },
                                });
                            }
                        }}
                    />
                </Affix>
            </Show>
            <Show when={inTabs !== undefined}>
                <Affix position={{ top: inTabs, right: 15 }}>
                    <Tooltip label={'View Associated Roles within Grid'}>
                        <ActionIcon
                            onClick={() => {
                                OpenModal.ContentModal({
                                    title: 'All Associated Roles',
                                    content: <AssociatedRolesInWorkflowComponent
                                        projectVersionId={versionId} workflowRepresentationId={workflowId} />,
                                });
                            }}
                            variant={'default'}
                            color={'var(--mantine-color-base-button)'}
                        >
                            <IconUsersGroup />
                        </ActionIcon>
                    </Tooltip>
                </Affix>
            </Show>
            <Show when={nodesToUpdate.length && editMode}>
                <Affix position={{ bottom: 25, right: 15 }}>
                    <Group>
                        <Button onClick={resetChanges}>
                            Reset
                        </Button>
                        <Button
                            onClick={() => {
                                nodesToUpdate.forEach((node) => {
                                    const resource = gridElementsQuery.data?.find(value => value.id === node.i);
                                    if (resource) {
                                        updateGridElement.mutate({
                                            id: node.i,
                                            resource: {
                                                ...resource,
                                                xIndex: node.x,
                                                yIndex: node.y,
                                                height: node.h,
                                                width: node.w,
                                            },
                                        });
                                    }
                                });
                                setNodesToUpdate([]);
                            }}
                        >
                            Save
                        </Button>
                    </Group>
                </Affix>
            </Show>
            <Card.Section>
                <div style={{
                    width: windowDimensions.width,
                    height: windowDimensions.height - 60,
                    margin: 'auto',
                    marginTop: 57,
                    backgroundColor: 'var(--mantine-color-body)',
                }}>
                    <ReactGridLayout
                        style={{ backgroundColor: 'var(--mantine-color-body)' }}
                        className="layout"
                        cols={12}
                        maxRows={8}
                        rowHeight={80}
                        allowOverlap={true}
                        // verticalCompact={false}
                        compactType={'vertical'}
                        onDragStop={(_, item1, item2) => {
                            if (JSON.stringify(item1) === JSON.stringify(item2)) {
                                return;
                            }
                            const newNodesToUpdate = nodesToUpdate.filter(value => value.i !== item2.i);
                            newNodesToUpdate.push(item2);
                            setNodesToUpdate(newNodesToUpdate);
                        }}
                        onResizeStop={(_, item1, item2) => {
                            if (JSON.stringify(item1) === JSON.stringify(item2)) {
                                return;
                            }
                            const newNodesToUpdate = nodesToUpdate.filter(value => value.i !== item2.i);
                            newNodesToUpdate.push(item2);
                            setNodesToUpdate(newNodesToUpdate);
                        }}

                    >
                        {nodes.map((value) => {
                            return (
                                <div key={value.id} data-grid={{
                                    x: value.xIndex,
                                    y: value.yIndex,
                                    w: value.width,
                                    h: value.height,
                                    resizeHandles: ['se'],
                                    isDraggable: editMode,
                                    isResizable: editMode,
                                }} style={{ display: 'flex' }}>
                                    <GridElementNode
                                        element={value}
                                        onEdit={openEditDialog}
                                        onDelete={deleteNode}
                                        editMode={editMode}
                                        processAreas={processAreasQuery.data ?? []}
                                        routeInfo={routeInfo}
                                        disabled={appliedFilter ? !appliedFilter.matchingGridElements.includes(value.id) : false}
                                    />
                                </div>
                            );
                        })}
                    </ReactGridLayout>
                </div>
            </Card.Section>
        </Card>
    );
};